import React, { useState } from 'react';
import { Calendar, Users, MapPin, Link2, MoveVertical, Save, X } from 'lucide-react';

const MilestoneManagement = ({ selectedMilestone, onMilestoneSelect }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    regulation: '',
    deadline: '',
    location: '',
    assignedTo: '',
    priority: 'medium',
    dependencies: []
  });

  // Mock milestones data
  const milestones = [
    {
      id: 1,
      title: 'Annual Safety Audit',
      regulation: 'OSHA 1910',
      deadline: '2025-01-15',
      location: 'Plant A',
      assignedTo: 'John Smith',
      priority: 'high',
      dependencies: [],
      criticalPath: true
    },
    {
      id: 2,
      title: 'Environmental Compliance Report',
      regulation: 'EPA Clean Air Act',
      deadline: '2025-01-20',
      location: 'All Sites',
      assignedTo: 'Sarah Johnson',
      priority: 'high',
      dependencies: [1],
      criticalPath: true
    },
    {
      id: 3,
      title: 'Fire Safety Certification Renewal',
      regulation: 'NFPA 101',
      deadline: '2025-02-01',
      location: 'Plant B',
      assignedTo: 'Mike Wilson',
      priority: 'medium',
      dependencies: [],
      criticalPath: false
    }
  ];

  const handleEdit = (milestone) => {
    setFormData({
      title: milestone?.title,
      regulation: milestone?.regulation,
      deadline: milestone?.deadline,
      location: milestone?.location,
      assignedTo: milestone?.assignedTo,
      priority: milestone?.priority,
      dependencies: milestone?.dependencies
    });
    setEditMode(true);
    onMilestoneSelect(milestone);
  };

  const handleSave = () => {
    // Save logic here
    setEditMode(false);
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData({
      title: '',
      regulation: '',
      deadline: '',
      location: '',
      assignedTo: '',
      priority: 'medium',
      dependencies: []
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Milestone List */}
      <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Milestones</h2>
          <p className="text-sm text-gray-600 mt-1">Click to view or edit</p>
        </div>

        <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
          {milestones?.map((milestone) => (
            <div
              key={milestone?.id}
              onClick={() => handleEdit(milestone)}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                selectedMilestone?.id === milestone?.id
                  ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-medium text-gray-900 text-sm">{milestone?.title}</h3>
                {milestone?.criticalPath && (
                  <span className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded">
                    Critical
                  </span>
                )}
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                <p>{milestone?.regulation}</p>
                <p>Due: {new Date(milestone.deadline)?.toLocaleDateString()}</p>
                <div className={`inline-block px-2 py-1 rounded border ${getPriorityColor(milestone?.priority)}`}>
                  {milestone?.priority} priority
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-gray-200">
          <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Add New Milestone
          </button>
        </div>
      </div>
      {/* Milestone Details & Edit Form */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {editMode ? 'Edit Milestone' : 'Milestone Details'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {editMode ? 'Update milestone information' : 'View milestone information'}
            </p>
          </div>
          {selectedMilestone && !editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Edit
            </button>
          )}
        </div>

        {editMode ? (
          <div className="p-6">
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Milestone Title *
                </label>
                <input
                  type="text"
                  value={formData?.title}
                  onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter milestone title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regulation/Standard *
                </label>
                <input
                  type="text"
                  value={formData?.regulation}
                  onChange={(e) => setFormData({ ...formData, regulation: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., OSHA 1910, EPA Clean Air Act"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deadline *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      value={formData?.deadline}
                      onChange={(e) => setFormData({ ...formData, deadline: e?.target?.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority *
                  </label>
                  <select
                    value={formData?.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData?.location}
                    onChange={(e) => setFormData({ ...formData, location: e?.target?.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter location"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To *
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData?.assignedTo}
                    onChange={(e) => setFormData({ ...formData, assignedTo: e?.target?.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter assignee name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dependencies
                </label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    multiple
                    value={formData?.dependencies}
                    onChange={(e) => setFormData({
                      ...formData,
                      dependencies: Array.from(e?.target?.selectedOptions, option => parseInt(option?.value))
                    })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {milestones?.filter(m => m?.id !== selectedMilestone?.id)?.map(m => (
                      <option key={m?.id} value={m?.id}>{m?.title}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleSave}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : selectedMilestone ? (
          <div className="p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Regulation/Standard</p>
                  <p className="font-medium text-gray-900">{selectedMilestone?.regulation}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Priority</p>
                  <span className={`inline-block px-3 py-1 rounded-lg border text-sm font-medium ${getPriorityColor(selectedMilestone?.priority)}`}>
                    {selectedMilestone?.priority}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Deadline</p>
                <p className="font-medium text-gray-900">
                  {new Date(selectedMilestone.deadline)?.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Location</p>
                <p className="font-medium text-gray-900">{selectedMilestone?.location}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">Assigned To</p>
                <p className="font-medium text-gray-900">{selectedMilestone?.assignedTo}</p>
              </div>

              {selectedMilestone?.criticalPath && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <MoveVertical className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-900">On Critical Path</span>
                  </div>
                  <p className="text-sm text-purple-700 mt-1">
                    This milestone is on the critical path. Delays will impact overall timeline.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Select a milestone to view details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MilestoneManagement;